<script lang="ts">
  import { onDestroy, onMount, untrack, type Snippet } from "svelte"
  import ProgressSteps, {
    type ProgressStep,
    type ProgressStepContext,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import type { MembershipRoleChoiceResponse } from "@kontextwerk/control-web-client"
  import type { IdentityAdministrationApi } from "../../../../../../../../../../../../api/kehrblatt/identity-administration"
  import type { MembershipInvitationsApi } from "../../../../../../../../../../../../api/kehrblatt/membership-invitations"
  import {
    LOCKED_WORKFLOW_STEP_MESSAGE,
    firstCurrentWorkflowStep,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../../../../../../_shared/_logic/workflow-progress"
  import UserDataStep from "./steps/user-data"
  import PermissionsStep from "./steps/permissions"
  import { issueInvitation } from "./_actions/issueInvitation"
  import {
    createUserCreateForm,
    validateUserDataStep,
    type UserCreateForm,
    type UserCreateOutputs,
    type UserCreateStepId,
  } from "./_shared/_logic/user-create-workflow"

  interface Props {
    /** Roles the actor may assign; captured when the overlay opens. */
    choices: readonly MembershipRoleChoiceResponse[]
    invitations: MembershipInvitationsApi
    access: IdentityAdministrationApi | null
    /** core.roles view: the chosen role's grants may be shown. */
    canViewRoles: boolean
    onInvited: () => void | Promise<unknown>
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
    setCloseDisabled?: (disabled: boolean) => void
  }

  let {
    choices,
    invitations,
    access,
    canViewRoles,
    onInvited,
    onremoveOverlay = () => {},
    onshowOverlay = () => {},
    setActionButtonSnippet = () => {},
    setCloseDisabled = () => {},
  }: Props = $props()

  const stepOrder: readonly UserCreateStepId[] = ["user_data", "permissions"]

  let form = $state<UserCreateForm>(untrack(() => createUserCreateForm(choices)))
  let outputs = $state<UserCreateOutputs>({})
  let selectedStep = $state<UserCreateStepId>("user_data")
  let submitting = $state(false)
  let destroyed = false

  const completedSteps = $derived<UserCreateStepId[]>(outputs.userData ? ["user_data"] : [])
  const currentStep = $derived(firstCurrentWorkflowStep({ stepOrder, completedSteps }))
  const selectedIndex = $derived(
    selectedWorkflowStepIndex({ stepOrder, completedSteps, selectedStep }),
  )

  const completeUserDataStep = () => {
    const result = validateUserDataStep({ form, choices })

    if ("message" in result) return result.message

    outputs = { userData: result.output }
    form = { ...form, email: result.output.email }

    return true
  }

  /** "Zurück" reopens the data step for editing; its output is recomputed on "Weiter". */
  const reopenUserDataStep = () => {
    if (submitting) return

    outputs = {}
    selectedStep = "user_data"
  }

  const submit = async () => {
    const userData = outputs.userData

    if (!userData || submitting) return

    submitting = true
    setCloseDisabled(true)
    let result: Awaited<ReturnType<typeof issueInvitation>>
    try {
      result = await issueInvitation({ api: invitations, userData })
    } finally {
      submitting = false

      if (!destroyed) setCloseDisabled(false)
    }

    if (destroyed) return

    if (result.status === "done") {
      newNotification({ class: "success", html: "Einladung wurde versendet." })
      void onInvited()
      onremoveOverlay()
    } else if (result.status === "conflict") {
      newNotification({
        class: "error",
        html: "Die Einladung konnte nicht ausgestellt werden. Bitte prüfen Sie E-Mail-Adresse und Rolle.",
      })
    } else if (result.status === "forbidden") {
      newNotification({ class: "error", html: "Für diese Einladung fehlt die Berechtigung." })
    } else if (result.status === "failed") {
      newNotification({
        class: "error",
        html: "Einladung fehlgeschlagen. Bitte versuchen Sie es später erneut.",
      })
    }
  }

  const steps = $derived<ProgressStep[]>([
    {
      id: "user_data",
      label: "Nutzerdaten",
      description: "Basisdaten für die Einladung erfassen.",
      actionLabel: "Weiter",
      workflowState: workflowStateForStep({ step: "user_data", currentStep, completedSteps }),
      onNext: completeUserDataStep,
      snippet: userDataStep,
    },
    {
      id: "permissions",
      label: "Berechtigungen",
      description: "Berechtigungen der gewählten Rolle prüfen.",
      hideDefaultAction: true,
      workflowState: workflowStateForStep({ step: "permissions", currentStep, completedSteps }),
      snippet: permissionsStep,
    },
  ])

  const handleSelectedIndexChange = (index: number) => {
    const next = stepOrder[index]

    if (next) selectedStep = next
  }

  onMount(() => {
    onshowOverlay()
    setActionButtonSnippet(null)
  })

  onDestroy(() => {
    destroyed = true
    setActionButtonSnippet(null)
  })
</script>

<div class="user-create-flow form light" data-testid="controlAdmin.users.invite.overlay">
  <ProgressSteps
    {steps}
    navigationMode="guarded"
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
    showControls={true}
    darkMode={true}
    syncUrl={false}
  />
</div>

{#snippet userDataStep(ctx: ProgressStepContext)}
  <UserDataStep
    {form}
    {choices}
    busy={ctx.loading || submitting}
    readOnly={ctx.readOnly}
    onFormChange={(nextForm) => {
      form = nextForm
    }}
  />
{/snippet}

{#snippet permissionsStep(_ctx: ProgressStepContext)}
  {#if outputs.userData}
    {#key outputs.userData.role.id}
      <PermissionsStep
        userData={outputs.userData}
        {access}
        {canViewRoles}
        {submitting}
        onPrevious={reopenUserDataStep}
        onSubmit={() => void submit()}
      />
    {/key}
  {/if}
{/snippet}

<style lang="less">
  .user-create-flow {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
</style>
